/**
 * Display identity for agent-authored comments. The backend stores the agent
 * id slug in `createdBy` and synthesizes `author.name` from it — this module
 * maps slugs to human display names (persona titles) and avatars.
 *
 * Exposed as a resolver hook so page-level surfaces (BriefCommentsSidebar)
 * can inject identity resolution into the target-agnostic comment thread via
 * `CommentThreadActions.resolveAgentIdentity` — `features/comment-on-file`
 * cannot import this feature directly (FSD boundaries).
 */
import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { AgentAvatar, SPECIAL_AGENT_IDS } from '@/entities/agent';
import { usePersonas } from '@/entities/persona';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { i18n } from '@/shared/i18n';

interface PersonaLike {
  id: string;
  personaTitle?: string | null;
  segmentName?: string | null;
  photoAssetId?: string | null;
}

export interface AgentIdentity {
  name: string;
  avatarSlot?: ReactNode;
}

export function resolveAgentDisplayName(
  agentId: string,
  personas: readonly PersonaLike[],
): { name: string; personaId: string | null } {
  const prefix = SPECIAL_AGENT_IDS.AUDIENCE_FLOW_PERSONA_PREFIX;
  if (agentId.startsWith(prefix)) {
    const personaId = agentId.slice(prefix.length);
    const persona = personas.find((p) => p.id === personaId);
    // Segment name is the app-wide audience display name; personaTitle is a
    // long tagline used only on persona detail surfaces.
    return {
      name:
        persona?.segmentName ??
        persona?.personaTitle ??
        i18n.t('chats:briefAgentReview.agentCommentIdentity.audience'),
      personaId,
    };
  }
  return { name: agentId, personaId: null };
}

/**
 * Returns a stable resolver mapping an agent id slug to its display identity.
 * Loads the workspace personas once; persona-prefixed agents get the persona's
 * title and photo, other agents fall back to their raw id slug.
 */
export function useAgentIdentityResolver(): (agentId: string) => AgentIdentity {
  const workspaceId = useCurrentWorkspaceId();
  const { data } = usePersonas(workspaceId);
  const personas: readonly PersonaLike[] = useMemo(() => data ?? [], [data]);

  return useCallback(
    (agentId: string): AgentIdentity => {
      const { name, personaId } = resolveAgentDisplayName(agentId, personas);
      const persona = personaId ? personas.find((p) => p.id === personaId) : undefined;
      return {
        name,
        avatarSlot: (
          <AgentAvatar
            agentId={agentId}
            agentTitle={name}
            variant="small"
            avatar={
              persona?.photoAssetId ? { type: 'assetId', assetId: persona.photoAssetId } : undefined
            }
          />
        ),
      };
    },
    [personas],
  );
}
